import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useDeliveryConfig } from "../contexts/SiteTextsContext";

export default function TermsOfUse() {
  const { auspost_content_visible } = useDeliveryConfig();
  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12 lg:py-20">
        <p className="text-[#9A7B4F] text-[11px] tracking-[0.3em] uppercase mb-3 text-center">Legal</p>
        <h1 className="text-3xl lg:text-4xl text-[#1A1A1A] text-center mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }} data-testid="terms-of-use-title">
          Terms of Use
        </h1>
        <p className="text-[13px] text-[#676664]/60 text-center mb-12">Last updated: February 2026</p>

        <div className="prose-mahra space-y-8 text-[#3A3A3A]" data-testid="terms-of-use-content">
          <Section title="1. Acceptance of Terms">
            <p>
              By accessing or using the Mahra website (shopmahra.com.au) and our fashion rental services, you agree to
              be bound by these Terms of Use. If you do not agree with any part of these terms, you must not use our
              website or services.
            </p>
            <p>
              These terms constitute a legally binding agreement between you ("the Customer", "you", "your") and
              Mahra ("we", "us", "our"), an Australian fashion rental business based in Victoria.
            </p>
          </Section>

          <Section title="2. Eligibility">
            <ul>
              <li>You must be at least 18 years of age to use our services.</li>
              <li>You must provide accurate and complete information when creating an account or making a booking.</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
            </ul>
          </Section>

          <Section title="3. Rental Agreement">
            <p>
              When you complete a booking through our website, you enter into a rental agreement with Mahra.
              The key terms of each rental are as follows:
            </p>
            <ul>
              <li><strong>Rental Period:</strong> The rental period begins on the delivery/pickup date and ends on the return-by date specified in your booking confirmation. Standard rental periods are 4 days or 8 days.</li>
              <li><strong>Rental Fee:</strong> The rental fee is the price displayed at the time of booking for the selected rental period and options. All prices are in Australian Dollars (AUD) and inclusive of GST where applicable.</li>
              <li><strong>Security Bond:</strong> A fully refundable security bond is charged at the time of booking. This bond will be refunded in full upon the return of the garment in its original condition, within the timeframe specified in your booking confirmation.</li>
            </ul>
          </Section>

          <Section title="4. Bookings and Payments">
            <ul>
              <li>All bookings are subject to availability and are confirmed only upon successful payment.</li>
              <li>Payments are processed securely through Stripe. By making a payment, you agree to Stripe's terms of service.</li>
              <li>We reserve the right to cancel any booking if we suspect fraudulent activity.</li>
              <li>Booking confirmation and rental details will be sent to the email address provided at checkout.</li>
            </ul>
          </Section>

          <Section title="5. Cancellation and Refund Policy">
            <p>Our cancellation policy is designed to be fair to both parties:</p>
            <ul>
              <li><strong>More than 14 days before event:</strong> Full refund of rental fee (security bond is always refunded).</li>
              <li><strong>7-14 days before event:</strong> 50% refund of rental fee.</li>
              <li><strong>3-7 days before event:</strong> 25% refund of rental fee.</li>
              <li><strong>Less than 3 days before event:</strong> No refund of rental fee. Bookings become non-refundable within 3 days of the event date.</li>
            </ul>
            <p>
              Security bonds are always refunded regardless of cancellation timing, provided no damage has occurred.
              Date changes may void cancellation eligibility.
            </p>
          </Section>

          <Section title="6. Delivery and Returns">
            <ul>
              <li><strong>Local Pickup:</strong> Available by appointment at our Oakleigh South, VIC location. Pickup details are shared after booking confirmation.</li>
              <li><strong>Local Delivery:</strong> Available within a 15km radius of Oakleigh South, VIC. A delivery fee applies. Garments are dispatched 1-2 days before the event date.</li>
              <li><strong>Returns:</strong> Garments must be returned by the return-by date specified in your booking. {auspost_content_visible ? "A prepaid Australia Post return label will be included with your order where applicable." : "Return postage is the customer's responsibility unless Mahra confirms otherwise in writing."}</li>
              <li><strong>Late Returns:</strong> Late returns may incur additional charges. We will notify you if your return is overdue, and fees may be charged to the payment method on file.</li>
            </ul>
          </Section>

          <Section title="7. Care of Rented Garments">
            <p>While garments are in your possession, you agree to:</p>
            <ul>
              <li>Handle all items with reasonable care and treat them as you would your own valuable clothing.</li>
              <li>Not alter, modify, wash, dry clean, or attempt to repair any garment.</li>
              <li>Not expose garments to excessive heat, direct sunlight, or hazardous substances (including but not limited to bleach, harsh chemicals, or excessive perfume).</li>
              <li>Return all garments and accessories (including dupatta, blouse, petticoat, and any other components) in the same condition as received.</li>
              <li>Report any damage or issues to us immediately upon discovery.</li>
            </ul>
            <p>
              We understand that minor wear may occur during normal use (e.g., light creasing). However, significant
              damage — including tears, stains, burns, missing components, or odour contamination — may result in
              charges against your security bond or additional fees.
            </p>
          </Section>

          <Section title="8. Damage and Loss">
            <ul>
              <li><strong>Minor Damage:</strong> Assessed on a case-by-case basis. Repair costs may be deducted from your security bond.</li>
              <li><strong>Major Damage or Loss:</strong> If a garment is damaged beyond repair or lost, you may be charged up to the full recommended retail price (RRP) of the garment. This charge may exceed the security bond amount.</li>
              <li><strong>Assessment Process:</strong> All garments are inspected upon return. We will provide photographic evidence and a detailed explanation of any deductions before processing charges.</li>
            </ul>
          </Section>

          <Section title="9. Intellectual Property">
            <p>
              All content on our website — including text, photographs, graphics, logos, icons, and software — is the
              property of Mahra or our licensors and is protected by Australian and international copyright laws.
              You may not reproduce, distribute, modify, or create derivative works from any content on our website
              without our prior written consent.
            </p>
            <p>
              Our product images may include AI-enhanced styling presentations. While the real garments closely
              reflect what is shown, minor variations in tone or detailing may exist. Authentic photos are available
              upon request.
            </p>
          </Section>

          <Section title="10. User Accounts">
            <ul>
              <li>You are responsible for all activity that occurs under your account.</li>
              <li>You must notify us immediately of any unauthorised use of your account.</li>
              <li>We reserve the right to suspend or terminate accounts that violate these terms.</li>
              <li>Account information must be kept accurate and up to date.</li>
            </ul>
          </Section>

          <Section title="11. Reviews and User Content">
            <p>
              By submitting reviews, feedback, or other content to our website, you grant Mahra a non-exclusive,
              royalty-free, perpetual licence to use, display, and distribute such content in connection with our
              services. You represent that any content you submit is truthful, does not infringe on any third-party
              rights, and does not contain defamatory or inappropriate material.
            </p>
            <p>
              We reserve the right to moderate, edit, or remove any user-submitted content at our discretion.
            </p>
          </Section>

          <Section title="12. Limitation of Liability">
            <p>
              To the maximum extent permitted by Australian Consumer Law:
            </p>
            <ul>
              <li>Our total liability to you for any claim arising from or related to our services shall not exceed the amount you paid for the relevant booking.</li>
              <li>We are not liable for any indirect, incidental, special, consequential, or punitive damages.</li>
              <li>We are not responsible for delays or failures caused by circumstances beyond our reasonable control (force majeure), including but not limited to natural disasters, postal service disruptions, or pandemics.</li>
            </ul>
            <p>
              Nothing in these terms excludes or limits consumer guarantees under the Australian Consumer Law that cannot be excluded or limited by law.
            </p>
          </Section>

          <Section title="13. Indemnification">
            <p>
              You agree to indemnify and hold harmless Mahra, its owners, employees, and agents from any claims,
              damages, losses, or expenses (including legal fees) arising from your use of our services, your
              violation of these terms, or your violation of any third-party rights.
            </p>
          </Section>

          <Section title="14. Governing Law">
            <p>
              These Terms of Use are governed by the laws of the State of Victoria, Australia. Any disputes arising
              from these terms or your use of our services shall be subject to the exclusive jurisdiction of the
              courts of Victoria, Australia.
            </p>
          </Section>

          <Section title="15. Modifications to Terms">
            <p>
              We reserve the right to modify these Terms of Use at any time. Changes will be posted on this page
              with an updated "Last updated" date. Your continued use of our website or services after any changes
              constitutes acceptance of the updated terms. We encourage you to review these terms periodically.
            </p>
          </Section>

          <Section title="16. Severability">
            <p>
              If any provision of these Terms of Use is found to be invalid, illegal, or unenforceable, the remaining
              provisions shall continue in full force and effect.
            </p>
          </Section>

          <Section title="17. Contact Us">
            <p>
              For any questions, concerns, or disputes regarding these Terms of Use, please contact us:
            </p>
            <ul>
              <li><strong>Contact:</strong> <a href="/contact" className="text-[#9A7B4F] underline underline-offset-2">Get in touch via our contact form</a></li>
              <li><strong>Location:</strong> Oakleigh South, VIC, Australia</li>
            </ul>
          </Section>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-lg text-[#1A1A1A] font-medium mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{title}</h2>
      <div className="text-[14px] leading-[1.85] space-y-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_li]:text-[14px] [&_li]:leading-[1.75]">
        {children}
      </div>
    </section>
  );
}
